import { EPermission } from 'models/permissions';

export interface INestedRoute {
  componentType: EPageComponentTypes;
  route: ERouteLinks;
  isProtected: boolean;
  permissions?: EPermission[];
  permissionOrCondition?: boolean;
}

export interface IPageBuilderConfig {
  componentType: EPageComponentTypes;
  route: ERouteLinks;
  isProtected: boolean;
  nestedRoutes?: INestedRoute[];
  permissions?: EPermission[];
  permissionOrCondition?: boolean;
  requiredWeaponLicenceModule?: boolean;
}

export enum ERouteLinks {
  Auth = '',
  AuthWithSlash = '/',
  ForgotPassword = '/forgot-password',
  ResetPassword = '/reset-password',
  ResetPasswordLink = ':token',
  Dashboard = '/dashboard',
  DashboardSearchResultsPage = '/search',
  LegalEntities = '/legal-entities',
  LegalEntitiesExpand = ':expand',
  AddLegalEntity = '/legal-entity/add',
  EditLegalEntity = '/legal-entity/edit/:id',
  DeleteLegalEntity = '/legal-entity/delete/:id',
  ViewLegalEntity = '/legal-entity/:id',
  LegalEntityAddImages = '/legal-entity/:id/add-images',
  LegalEntityEditImages = '/legal-entity/:id/edit-images',
  ViewLegalEntityFirearm = '/legal-entity-firearm/:id',
  LegalEntityAddGroup = '/legal-entity/:id/group/add',
  LegalEntityGroupFirearmSearch = '/legal-entity/:id/group/search-firearms',
  LegalEntityEditGroup = '/legal-entity/:id/group/edit/:groupId',
  LegalEntityDeleteGroup = '/legal-entity/:id/group/delete/:groupId',
  ViewLegalEntityGroup = '/legal-entity/:id/group/:groupId',
  LegalEntityGroupAddFirearm = '/legal-entity/:id/group/:groupId/firearm/add',
  LegalEntityGroupDeleteFirearm = '/legal-entity/:id/group/:groupId/firearm/delete/:firearmId',
  LegalEntityAddComment = '/legal-entity/:id/comment/add',
  LegalEntityDeleteComment = '/legal-entity/:id/comment/delete/:commentId',
  LegalEntityAddAddress = '/legal-entity/:id/address/add',
  LegalEntityEditAddress = '/legal-entity/:id/address/edit/:addressId',
  ViewLegalEntityAddressMap = '/legal-entity/:id/address/:addressId',
  LegalEntityDeleteAddress = '/legal-entity/:id/address/delete/:addressId',
  LegalEntityAddAttachment = '/legal-entity/:id/attachment/add',
  LegalEntityViewLicence = '/legal-entity/:id/licence/:licenceId',
  LegalEntityLicenceLinkWeapon = '/legal-entity/:id/licence/:licenceId/link-weapon',
  LegalEntityLicenceLinkWeaponVaryRenew = '/legal-entity/:id/licence/:licenceId/link-weapon/vary-renew',
  LegalEntityLicenceVary = '/legal-entity/:id/licence/:licenceId/vary',
  LegalEntityLicenceRenew = '/legal-entity/:id/licence/:licenceId/renew',
  LegalEntityLicenceVaryFromLE = '/legal-entity/:id/licence/:licenceId/vary-le',
  LegalEntityLicenceRenewFromLE = '/legal-entity/:id/licence/:licenceId/renew-le',
  DetailLicenceRenew = '/licences/:licenceId/renew',
  DetailLicenceVary = '/licences/:licenceId/vary',
  LegalEntityCaseLicenceRenewPage = '/legal-entity/:id/case/:caseId/licence/:licenceId/renew',
  LegalEntityCaseLicenceVaryPage = '/legal-entity/:id/case/:caseId/licence/:licenceId/vary',
  CaseRenewLicencePage = '/case-management/case/:caseId/licence/:licenceId/renew',
  CaseVaryLicencePage = '/case-management/case/:caseId/licence/:licenceId/vary',

  DetailLicence = '/licences/:licenceId',
  PrintoutBatchDetails = '/licences/batches/:id',
  PrintoutDetails = '/licences/printouts/:id',
  LicenceLinkWeapon = '/licence/:licenceId/link-weapon',
  LicenceLinkWeaponVaryRenew = '/licence/:licenceId/link-weapon/vary-renew',
  LegalEntityAddLicenceUnique = '/legal-entity/:id/licence/add/:licenceId',
  LegalEntityAddContact = '/legal-entity/:id/contact/add',
  LegalEntityEditContact = '/legal-entity/:id/contact/edit/:contactId',
  LegalEntityDeleteContact = '/legal-entity/:id/contact/delete/:contactId',
  LegalEntityAddPreviousNamesSurnames = '/legal-entity/:id/previousNamesSurnames/add',
  LegalEntityEditPreviousNamesSurnames = '/legal-entity/:id/previousNamesSurnames/edit/:previousNamesSurnamesId',
  LegalEntityDeletePreviousNamesSurnames = '/legal-entity/:id/previousNamesSurnames/delete/:previousNamesSurnamesId',
  LegalEntityAddFirearm = '/legal-entity/:id/firearm/add',
  LegalEntityAddTransactionForGroup = '/legal-entity/:id/transaction-for-group/:groupId/add',
  LegalEntityAddTransactionForAncillaryGroup = '/legal-entity/:id/transaction-for-ancillary-group/:groupId/add',
  LegalEntityAddAncillary = '/legal-entity/:id/ancillary/add',
  LegalEntityViewAncillary = '/legal-entity/:leId/ancillary/:type/:id',
  LegalEntityAddAncillaryGroup = '/legal-entity/:id/ancillary-group/add',
  ViewLegalEntityAncillaryGroup = '/legal-entity/:id/ancillary-group/:groupId',
  LegalEntityGroupDeleteAncillary = '/legal-entity/:id/ancillary-group/:groupId/ancillary/delete/:ancillaryType/:ancillaryId',
  LegalEntityAddAncillaryGroupSearch = '/legal-entity/:id/ancillary-group/search-ancillaries',
  AddToAncillaryGroup = '/legal-entity/:id/ancillary-group/add-to-group/:groupId',
  EditAncillaryGroup = '/legal-entity/:id/ancillary-group/edit/:groupId',
  DeleteAncillaryGroup = '/legal-entity/:id/ancillary-group/delete/:groupId',
  MiddleTransaction = '/registered-firearms/:id/transaction/add',
  SearchForFRTFirearm = '/legal-entity/:id/firearm/add/search',
  SearchForARTAncillary = '/legal-entity/:id/ancillary/add/search',
  FRTFirearmDetails = '/legal-entity/:id/firearm/add/details/:frtId',
  SelectLocation = '/legal-entity/:id/firearm/add/location',
  Firearms = '/firearms',
  ViewRegisteredFirearm = '/registered-firearms/:id',
  FirearmAddImages = '/registered-firearms/:id/add-images',
  FirearmEditImages = '/registered-firearms/:id/edit-images',
  ViewRegisteredFirearmFromLicence = '/legal-entity/:legalEntityId/licence/:licenceId/registered-firearms/:id',
  EditRegisteredFirearm = '/registered-firearms/edit/:id',
  DisableRegisteredFirearm = '/registered-firearms/disable/:id',
  ViewRegisteredFirearmTransaction = '/registered-firearm-transaction/:id',
  RegisteredFirearmAddComment = '/registered-firearms/:id/comment/add',
  RegisteredFirearmDeleteComment = '/registered-firearms/:id/comment/delete/:commentId',
  RegisteredFirearmAddAttachment = '/registered-firearms/:id/attachment/add',
  RegisteredFirearmDeleteAttachment = '/registered-firearms/:id/attachment/delete/:attachmentId',
  ViewRegisteredFirearmAttachment = '/registered-firearms/:id/attachment/preview/:attachmentId',
  RegisteredFirearmsMap = '/registered-firearms-map',
  MoveTransaction = '/transactions/move',
  RegisteredAncillariesMap = '/registered-ancillaries-map',
  SelectLegalEntity = '/select-legal-entity',
  FRTDetails = '/firearms/frt/:frtId',
  FRTAddImages = '/firearms/frt/:frtId/add-images',
  FRTEditImages = '/firearms/frt/:frtId/edit-images',
  FRTAddCalibre = '/firearms/frt/:frtId/add-calibre',
  FRTEditCalibre = '/firearms/frt/:frtId/:specificationId/edit-calibre',
  AddFRT = '/firearms/frt/add',
  EditFRT = '/firearms/frt/edit/:frtId',
  Ancillaries = '/ancillaries',
  ViewAncillaries = '/ancillaries-reference-table/:type/:id',
  EditAncillaries = '/ancillaries-reference-table/:type/edit/:id',
  AddAncillaries = '/ancillaries-reference-table/add',
  AncillariesAddImages = '/ancillaries-reference-table/:type/:id/add-images',
  AncillariesEditImages = '/ancillaries-reference-table/:type/:id/edit-images',
  ViewAccessories = '/ancillaries/accessory/:id',
  ViewAmmunition = '/ancillaries/ammunition/:id',
  ViewComponents = '/ancillaries/component/:id',
  AccessoriesAddImages = '/ancillaries/accessory/:id/add-images',
  AmmunitionAddImages = '/ancillaries/ammunition/:id/add-images',
  ComponentsAddImages = '/ancillaries/component/:id/add-images',
  AccessoriesEditImages = '/ancillaries/accessory/:id/edit-images',
  AmmunitionEditImages = '/ancillaries/ammunition/:id/edit-images',
  ComponentsEditImages = '/ancillaries/component/:id/edit-images',
  EditAccessories = '/ancillaries/accessory/edit/:id',
  EditComponents = '/ancillaries/component/edit/:id',
  ViewAncillaryDetails = '/ancillaries-reference-table-details/:type/:id',
  AccessoriesDeleteComment = '/ancillaries/accessory/:id/comment/delete/:commentId',
  AccessoriesAddComment = '/ancillaries/accessory/:id/comment/add',
  AccessoriesAddAttachment = '/ancillaries/accessory/:id/attachment/add',
  AccessoriesViewAttachment = '/ancillaries/accessory/:ancillaryId/attachment/:id',
  AccessoriesDeleteAttachment = '/ancillaries/accessory/:ancillaryId/attachment/:id/delete',
  ComponentsDeleteComment = '/ancillaries/component/:id/comment/delete/:commentId',
  ComponentsAddComment = '/ancillaries/component/:id/comment/add',
  ComponentsAddAttachment = '/ancillaries/component/:id/attachment/add',
  ComponentsViewAttachment = '/ancillaries/component/:ancillaryId/attachment/:id',
  ComponentsDeleteAttachment = '/ancillaries/component/:ancillaryId/attachment/:id/delete',
  AmmunitionDeleteComment = '/ancillaries/ammunition/:id/comment/delete/:commentId',
  AmmunitionAddComment = '/ancillaries/ammunition/:id/comment/add',
  AmmunitionAddAttachment = '/ancillaries/ammunition/:id/attachment/add',
  AmmunitionViewAttachment = '/ancillaries/ammunition/:ancillaryId/attachment/:id',
  AmmunitionDeleteAttachment = '/ancillaries/ammunition/:ancillaryId/attachment/:id/delete',
  ViewAncillary = '/ancillaries/:type/:id',
  ViewAncillaryGroup = '/ancillary-group/:type/:id',
  EditAncillary = '/ancillaries/:type/edit/:id',
  AddAncillariesSearchForFRTFirearm = '/ancillaries/add/firearm/search',
  AncillariesMiddleTransaction = '/ancillaries/:id/transaction/add',
  DisableAncillary = '/ancillaries/disable/:type/:id',
  Transactions = '/transactions',
  ViewTransaction = '/transactions/:id',
  CancelTransaction = '/transactions/:id/cancel',
  TransactionAddComment = '/transactions/:id/comment/add',
  TransactionDeleteComment = '/transactions/:id/comment/delete/:commentId',
  SaveTransactionPreset = '/transactions/:type/save-preset',
  SaveAncillariesPreset = '/ancillary/:type/save-preset',
  Users = '/users',
  AddUser = '/users/add',
  EditUser = '/users/edit/:id',
  GeneratePasswordUser = '/users/generate-password/:id',
  ViewUser = '/users/:id',
  EditUserStatus = '/users/:id/:status',
  ResetUserOfflineAuth = '/users/:id/offline-authentication',
  AssignLESearch = '/users/:id/legal-entities',
  AssignLE = '/users/:id/legal-entity/assign/:LEId',
  DeleteAssignedLE = '/users/:id/legal-entity/delete/:LEId',
  DeleteUser = '/users/delete/:id',
  ActivateUser = '/users/activate/:id',
  DeactivateUser = '/users/deactivate/:id',
  Roles = '/roles',
  AddRole = '/role/add',
  EditRole = '/role/edit/:id',
  DeleteRole = '/role/delete/:id',
  ViewRole = '/role/:id',
  CustomFields = '/custom-fields',
  LicenceDetails = '/licence-details',
  LicenceReplace = '/licence/replace',
  AddCustomField = '/custom-field/add',
  EditCustomField = '/custom-field/edit/:id',
  ToggleCustomField = '/custom-field/:toggleType/:id',
  Administration = '/administration',
  Notifications = '/notifications',
  ViewNotification = '/notification/:id',
  TableTest = '/table',
  StyleGuide = '/styleguide',
  Account = '/account',
  AccountRemoveDevice = '/account/remove-device/:id',
  AccountChangePassword = '/account/change-password',
  MyFilters = '/account/my-filters',
  DeleteFilter = '/account/my-filters/delete/:id',
  AccountEdit = '/account/edit',
  NotFoundPage = '*',
  AccessDeniedPage = '/access-denied',
  ServerErrorPage = '/internal-server-error',
  SavePreset = '/legal-entity/:type/save-preset',
  SaveFirearmsPreset = '/firearms/:type/save-preset',
  SaveLicencesPreset = '/licences/:type/save-preset',
  Dictionaries = '/dictionaries',
  ViewDictionary = '/dictionary/:type',
  AddDictionaryValue = '/dictionary/:type/add',
  EditDictionaryValue = '/dictionary/:type/edit/:id',
  AddDictionary = '/dictionary/add',
  EditDictionary = '/dictionary/edit/:id',
  GeneralLicenceSettings = '/general-licence-settings',
  LicenceAuthorities = '/licence-authorities',
  AddLicenceAuthorities = '/licence-authorities/add',
  EditLicenceAuthorities = '/licence-authorities/:id/edit',
  DetailsLicenceAuthorities = '/licence-authorities/:id',
  DetailsLicenceAuthoritiesPrintoutSettings = '/licence-authorities/:id/printout-settings',
  EditLicenceAuthoritiesPrintoutSettings = '/licence-authorities/:id/printout-settings/edit',
  DeleteLicenceAuthoritiesPrintoutSettings = '/licence-authorities/:id/printout-settings/delete',
  AddLicenceAuthoritiesPrintoutSettings = '/licence-authorities/:id/printout-settings/add',
  AuthenticationSetup = '/settings/authentication',
  DateTimeSetup = '/settings/date-time',
  LocationMapSetup = '/settings/location-map',
  NotificationsSetup = '/settings/notifications',
  LogosSetupPage = '/settings/logos-setup',
  SystemFieldsConfigurationPage = '/settings/system-fields-configuration',
  DocumentsGeneratingSetupPage = '/settings/documents-generating',
  UnitsCurrencySetupPage = '/settings/units-currency',
  AuditLogPage = '/audit-log',
  AuditLogViewPage = '/audit-log/:id',
  ApiKeysPage = '/api-keys',
  AddApiKeyPage = '/api-keys/add',
  EditApiKeyPage = '/api-keys/edit/:id',
  ViewApiKeyPage = '/api-key/:id',
  DeleteApiKeyPage = '/api-key/delete/:id',
  StartLicencePage = '/init',
  LicenceConfigurationPage = '/licence-configuration',
  AddLicenceTemplatePage = '/licence-configuration/add',
  EditLicenceTemplatePage = '/licence-configuration/edit/:id',
  LicenceTemplatePage = '/licence-configuration/detail/:id',
  LicencePrintoutSettings = '/licence-configuration/detail/:id/printout-settings',
  AddLicenceTemplateApplicationProcessPage = '/licence-configuration/detail/:id/application-process/add',
  EditLicenceTemplateApplicationProcessSettingsPage = '/licence-configuration/detail/:id/application-process/:processId/settings/edit',
  EditLicenceTemplateApplicationProcessPricePage = '/licence-configuration/detail/:id/application-process/:processId/price/edit',
  LicenceTemplateApplicationProcessPage = '/licence-configuration/detail/:id/application-process/:processId',
  LicencesPage = '/licences',
  CalendarPage = '/calendar',

  CaseManagementPage = '/case-management',
  CasesDetailsPage = '/case-management/case/:caseId',
  LegalEntityCasesDetailsPage = '/legal-entity/:id/case/:caseId',
  CaseAddPage = '/case-management/case/add',
  LegalEntityCaseAddPage = '/legal-entity/:id/case/add',
  LegalEntityAddLicenceByCase = '/legal-entity/:id/case/:caseId/licence/add',
  LegalEntityCreateLicenceVaryCase = '/legal-entity/:id/case/:caseId/licence/:licenceId/create-vary',
  LegalEntityCreateLicenceRenewCase = '/legal-entity/:id/case/:caseId/licence/:licenceId/create-renew',

  CaseNoteAddPage = '/case-management/case/:caseId/notes/add',
  LegalEntityCaseNoteAddPage = '/legal-entity/:id/case/:caseId/notes/add',
  CaseNotePage = '/case-management/case/:caseId/notes/:noteId',
  LegalEntityCaseNotePage = '/legal-entity/:id/case/:caseId/notes/:noteId',
  CaseNoteEditPage = '/case-management/case/:caseId/notes/:noteId/edit',
  LegalEntityCaseNoteEditPage = '/legal-entity/:id/case/:caseId/notes/:noteId/edit',

  CaseDetailsReviewCommentPage = '/case-management/case/:caseId/review-comment/:fileId',
  LegalEntityCaseDetailsReviewCommentPage = '/legal-entity/:id/case/:caseId/review-comment/:fileId',

  AddIntelligenceReportResult = '/case-management/case/:caseId/report/:reportId/add',
  LegalEntityAddIntelligenceReportResult = '/legal-entity/:id/case/:caseId/report/:reportId/add',
  CaseIntelligenceReportPage = '/case-management/case/:caseId/report/:reportId',
  LegalEntityCaseIntelligenceReportPage = '/legal-entity/:id/case/:caseId/report/:reportId',
  CaseIntelligenceReportEditPage = '/case-management/case/:caseId/report/:reportId/edit',
  LegalEntityCaseIntelligenceReportEditPage = '/legal-entity/:id/case/:caseId/report/:reportId/edit',

  LegalEntityCaseAddLicencePage = '/legal-entity/:id/case/:caseId/licence/add',
  CaseAddLicencePage = '/case-management/case/:caseId/licence/add',
  CaseCreateLicenceVary = '/case-management/case/:caseId/licence/:licenceId/create-vary',
  CaseCreateLicenceRenew = '/case-management/case/:caseId/licence/:licenceId/create-renew',

  CaseDetailLicencePage = '/case-management/case/:caseId/licence/:licenceId',
  LegalEntityCaseDetailLicencePage = '/legal-entity/:id/case/:caseId/licence/:licenceId',
}

export enum EPageComponentTypes {
  AuthPage = 'AuthPage',
  ForgotPasswordPage = 'ForgotPasswordPage',
  ResetPasswordPage = 'ResetPasswordPage',
  ResetPasswordLink = 'ResetPasswordPage',
  DashboardPage = 'DashboardPage',
  DashboardSearchResultsPage = 'DashboardSearchResultsPage',
  LegalEntitiesPage = 'LegalEntitiesPage',
  LegalEntitiesExpand = 'LegalEntitiesPage',
  AddLegalEntityPage = 'AddLegalEntityPage',
  EditLegalEntityPage = 'EditLegalEntityPage',
  DeleteLegalEntityPage = 'DeleteLegalEntityPage',
  ViewLegalEntityPage = 'ViewLegalEntityPage',
  LegalEntityAddGroupPage = 'LegalEntityAddGroupPage',
  LegalEntityGroupFirearmSearchPage = 'LegalEntityGroupFirearmSearchPage',
  LegalEntityEditGroupPage = 'LegalEntityEditGroupPage',
  LegalEntityDeleteGroupPage = 'LegalEntityDeleteGroupPage',
  LegalEntityAddCommentPage = 'LegalEntityAddCommentPage',
  LegalEntityDeleteCommentPage = 'LegalEntityDeleteCommentPage',
  LegalEntityAddAddressPage = 'LegalEntityAddAddressPage',
  LegalEntityEditAddressPage = 'LegalEntityEditAddressPage',
  ViewLegalEntityAddressMapPage = 'ViewLegalEntityAddressMapPage',
  LegalEntityDeleteAddressPage = 'LegalEntityDeleteAddressPage',
  LegalEntityAddAttachmentPage = 'LegalEntityAddAttachmentPage',
  LegalEntityAddContactPage = 'LegalEntityAddContactPage',
  LegalEntityEditContactPage = 'LegalEntityEditContactPage',
  LegalEntityDeleteContactPage = 'LegalEntityDeleteContactPage',
  LegalEntityAddPreviousNamesSurnamesPage = 'LegalEntityAddPreviousNamesSurnamesPage',
  LegalEntityEditPreviousNamesSurnamesPage = 'LegalEntityEditPreviousNamesSurnamesPage',
  LegalEntityDeletePreviousNamesSurnamesPage = 'LegalEntityDeletePreviousNamesSurnamesPage',
  ViewLegalEntityGroupPage = 'ViewLegalEntityGroupPage',
  LegalEntityGroupAddFirearmPage = 'LegalEntityGroupAddFirearm',
  LegalEntityGroupDeleteFirearmPage = 'LegalEntityGroupDeleteFirearm',
  LegalEntityGroupDeleteAncillaryPage = 'LegalEntityGroupDeleteAncillary',
  LegalEntityAddFirearmPage = 'LegalEntityAddFirearmPage',
  LegalEntityAddTransactionForGroupPage = 'LegalEntityAddTransactionForGroupPage',
  LegalEntityAddTransactionForAncillaryGroupPage = 'LegalEntityAddTransactionForAncillaryGroupPage',
  LegalEntityAddAncillaryPage = 'LegalEntityAddAncillaryPage',
  LegalEntityAddAncillaryGroupPage = 'LegalEntityAddAncillaryGroupPage',
  ViewLegalEntityAncillaryGroupPage = 'ViewLegalEntityAncillaryGroupPage',
  LegalEntityEditAncillaryGroupPage = 'LegalEntityEditAncillaryGroupPage',
  LegalEntityDeleteAncillariesGroupPage = 'LegalEntityDeleteAncillariesGroupPage',
  LegalEntityAddAncillaryGroupSearchPage = 'LegalEntityAddAncillaryGroupSearchPage',
  SearchForFRTFirearmPage = 'SearchForFRTFirearmPage',
  SearchForARTAncillaryPage = 'SearchForARTAncillaryPage',
  FRTFirearmDetailsPage = 'FRTFirearmDetailsPage',
  FRTDetailsPage = 'FRTDetailsPage',
  FRTAddCalibrePage = 'FRTAddCalibrePage',
  FRTEditCalibrePage = 'FRTEditCalibrePage',
  EditFRTPage = 'EditFRTPage',
  AddFRTPage = 'AddFRTPage',
  SelectLocationPage = 'SelectLocationPage',
  MoveTransactionPage = 'MoveTransactionPage',
  FirearmsPage = 'FirearmsPage',
  ViewRegisteredFirearmPage = 'ViewRegisteredFirearmPage',
  ViewRegisteredFirearmFromLicencePage = 'ViewRegisteredFirearmFromLicencePage',
  EditRegisteredFirearmPage = 'EditRegisteredFirearmPage',
  DisableRegisteredFirearmPage = 'DisableRegisteredFirearmPage',
  ViewRegisteredFirearmAttachment = 'ViewRegisteredFirearmAttachment',
  RegisteredFirearmAddCommentPage = 'RegisteredFirearmAddCommentPage',
  RegisteredFirearmDeleteCommentPage = 'RegisteredFirearmDeleteCommentPage',
  RegisteredFirearmDeleteAttachmentPage = 'RegisteredFirearmDeleteAttachmentPage',
  RegisteredFirearmAddAttachmentPage = 'RegisteredFirearmAddAttachmentPage',
  RegisteredFirearmsMapPage = 'RegisteredFirearmsMapPage',
  RegisteredAncillariesMapPage = 'RegisteredAncillariesMapPage',
  SelectLegalEntityPage = 'SelectLegalEntityPage',
  AncillariesAddTransactionPage = 'AncillariesAddTransactionPage',
  AncillariesPage = 'AncillariesPage',
  AddAncillaries = 'AddAncillaries',
  EditAncillaries = 'EditAncillaries',
  EditAncillary = 'EditAncillary',
  DetailAncillaries = 'DetailAncillaries',
  AncillaryDetails = 'AncillaryDetails',
  DetailAccessories = 'DetailAccessories',
  DetailComponents = 'DetailComponents',
  DetailAmmunition = 'DetailAmmunition',
  DisableAncillary = 'DisableAncillary',
  AccessoriesDeleteCommentPage = 'AccessoriesDeleteCommentPage',
  AccessoriesAddCommentPage = 'AccessoriesAddCommentPage',
  AmmunitionDeleteCommentPage = 'AmmunitionDeleteCommentPage',
  AmmunitionAddCommentPage = 'AmmunitionAddCommentPage',
  ComponentsDeleteCommentPage = 'ComponentsDeleteCommentPage',
  ComponentsAddCommentPage = 'ComponentsAddCommentPage',
  AccessoriesAddAttachmentPage = 'AccessoriesAddAttachmentPage',
  AccessoriesViewAttachmentPage = 'AccessoriesViewAttachmentPage',
  AccessoriesDeleteAttachmentPage = 'AccessoriesDeleteAttachmentPage',
  AmmunitionAddAttachmentPage = 'AmmunitionAddAttachmentPage',
  AmmunitionViewAttachmentPage = 'AmmunitionViewAttachmentPage',
  AmmunitionDeleteAttachmentPage = 'AmmunitionDeleteAttachmentPage',
  ComponentsAddAttachmentPage = 'ComponentsAddAttachmentPage',
  ComponentsViewAttachmentPage = 'ComponentsViewAttachmentPage',
  ComponentsDeleteAttachmentPage = 'ComponentsDeleteAttachmentPage',
  TransactionsPage = 'TransactionsPage',
  ViewTransactionPage = 'ViewTransactionPage',
  CancelTransactionPage = 'CancelTransactionPage',
  TransactionAddCommentPage = 'TransactionAddCommentPage',
  TransactionDeleteCommentPage = 'TransactionDeleteCommentPage',
  SaveTransactionPresetPage = 'SaveTransactionPresetPage',
  UsersPage = 'UsersPage',
  AddUserPage = 'AddUserPage',
  EditUserPage = 'EditUserPage',
  GeneratePasswordUserPage = 'GeneratePasswordUserPage',
  ViewUserPage = 'ViewUserPage',
  EditUserStatusPage = 'EditUserStatusPage',
  ResetUserOfflineAuth = 'ResetUserOfflineAuth',
  AssignLESearchPage = 'AssignLESearchPage',
  AssignLEPage = 'AssignLEPage',
  DeleteAssignedLEPage = 'DeleteAssignedLEPage',
  RolesPage = 'RolesPage',
  DictionariesPage = 'DictionariesPage',
  LicencePage = 'LicencePage',
  ReplaceLicencePage = 'ReplaceLicencePage',
  ViewDictionaryPage = 'ViewDictionaryPage',
  EditDictionaryPage = 'EditDictionaryPage',
  AddDictionaryValuePage = 'AddDictionaryValuePage',
  AddRolePage = 'AddRolePage',
  EditRolePage = 'EditRolePage',
  DeleteRolePage = 'DeleteRolePage',
  ViewRolePage = 'ViewRolePage',
  AdministrationPage = 'AdministrationPage',
  GeneralLicenceSettingsPage = 'GeneralLicenceSettingsPage',
  LicenceAuthoritiesPage = 'LicenceAuthoritiesPage',
  AddLicenceAuthorityPage = 'AddLicenceAuthorityPage',
  EditLicenceAuthorityPage = 'EditLicenceAuthorityPage',
  LicenceAuthorityPage = 'LicenceAuthorityPage',
  LicenceAuthoritiesPrintoutSettingsPage = 'LicenceAuthoritiesPrintoutSettingsPage',
  LicenceAuthoritiesPrintoutSettingsEdit = 'LicenceAuthoritiesPrintoutSettingsEdit',
  LicenceAuthoritiesPrintoutSettingsDelete = 'LicenceAuthoritiesPrintoutSettingsDelete',
  LicenceAuthoritiesPrintoutSettingsAdd = 'LicenceAuthoritiesPrintoutSettingsAdd',
  NotificationsSetupPage = 'NotificationsSetupPage',
  AuthenticationSetupPage = 'AuthenticationSetupPage',
  DateTimeSetupPage = 'DateTimeSetupPage',
  LogosSetupPage = 'LogosSetupPage',
  SystemFieldsConfigurationPage = 'SystemFieldsConfigurationPage',
  DocumentsGeneratingSetupPage = 'DocumentsGeneratingSetupPage',
  UnitsCurrencySetupPage = 'UnitsCurrencySetupPage',
  AuditLogPage = 'AuditLogPage',
  AuditLogViewPage = 'AuditLogViewPage',
  CustomFieldsPage = 'CustomFieldsPage',
  AddCustomFieldPage = 'AddCustomFieldPage',
  ToggleCustomFieldPage = 'ToggleCustomFieldPage',
  EditCustomFieldPage = 'EditCustomFieldPage',
  NotificationsPage = 'NotificationsPage',
  ViewNotificationPage = 'ViewNotificationPage',
  AccountPage = 'AccountPage',
  TableTestPage = 'TableTestPage',
  NotFoundPage = 'NotFoundPage',
  AccessDeniedPage = 'AccessDeniedPage',
  ServerErrorPage = 'ServerErrorPage',
  StyleGuidePage = 'StyleGuidePage',
  RemoveDevicePage = 'RemoveDevicePage',
  ChangePasswordPage = 'ChangePasswordPage',
  AccountEditPage = 'EditAccountPage',
  MyFiltersPage = 'MyFiltersPage',
  DeleteFilterPage = 'DeleteFilterPage',
  SavePresetPage = 'SavePresetPage',
  SaveFirearmsPresetPage = 'SaveFirearmsPresetPage',
  SaveAncillariesPresetPage = 'SaveAncillariesPresetPage',
  SaveLicencesPresetPage = 'SaveLicencesPresetPage',
  ApiKeysPage = 'ApiKeysPage',
  AddApiKeyPage = 'AddApiKeyPage',
  EditApiKeyPage = 'EditApiKeyPage',
  ViewApiKeyPage = 'ViewApiKeyPage',
  DeleteApiKeyPage = 'DeleteApiKeyPage',
  StartLicencePage = 'StartLicencePage',
  DeleteUserPage = 'DeleteUserPage',
  LicenceConfigurationPage = 'LicenceConfigurationPage',
  AddLicenceTemplatePage = 'AddLicenceTemplatePage',
  LicenceTemplatePage = 'LicenceTemplatePage',
  LicencePrintoutSettings = 'LicencePrintoutSettings',
  AddLicenceTemplateApplicationProcessPage = 'AddLicenceTemplateApplicationProcessPage',
  EditLicenceTemplateApplicationProcessPage = 'EditLicenceTemplateApplicationProcessPage',
  LicenceTemplateApplicationProcessPage = 'LicenceTemplateApplicationProcessPage',

  EditLicenceTemplatePage = 'EditLicenceTemplatePage',
  LegalEntityViewLicencePage = 'LegalEntityViewLicencePage',
  LegalEntityLicenceLinkWeaponPage = 'LegalEntityLicenceLinkWeaponPage',
  LegalEntityLicenceVaryPage = 'LegalEntityLicenceVaryPage',
  LegalEntityCreateLicenceVaryPage = 'LegalEntityCreateLicenceVaryPage',
  LegalEntityCreateLicenceRenewPage = 'LegalEntityCreateLicenceRenewPage',
  LegalEntityLicenceRenewPage = 'LegalEntityLicenceRenewPage',
  LegalEntityLicencePage = 'LegalEntityLicencePage',
  CaseManagementPage = 'CaseManagementPage',
  CaseAddPage = 'CaseAddPage',
  LegalEntityCaseAddPage = 'LegalEntityCaseAddPage',
  AddIntelligenceReportResult = 'AddIntelligenceReportResult',
  LegalEntityAddIntelligenceReportResult = 'LegalEntityAddIntelligenceReportResult',
  CaseIntelligenceReportPage = 'CaseIntelligenceReportPage',
  LegalEntityCaseIntelligenceReportPage = 'LegalEntityCaseIntelligenceReportPage',
  CaseIntelligenceReportEditPage = 'CaseIntelligenceReportEditPage',
  LegalEntityCaseIntelligenceReportEditPage = 'LegalEntityCaseIntelligenceReportEditPage',
  CaseDetailsPage = 'CaseDetailsPage',
  CaseDetailsReviewCommentPage = 'CaseDetailsReviewCommentPage',
  CaseNoteAddPage = 'CaseNoteAddPage',
  LegalEntityCaseNoteAddPage = 'LegalEntityCaseNoteAddPage',
  CaseNotePage = 'CaseNotePage',
  LegalEntityCaseNotePage = 'LegalEntityCaseNotePage',
  CaseNoteEditPage = 'CaseNoteEditPage',
  LegalEntityCaseNoteEditPage = 'LegalEntityCaseNoteEditPage',
  CalendarPage = 'CalendarPage',
  LicencesPage = 'LicencesPage',
  PrintoutBatchDetails = 'PrintoutBatchDetails',
  PrintoutDetails = 'PrintoutDetails',
  ManageImagesTemplate = 'ManageImagesTemplate',
}
